import { useState, useEffect, useRef } from 'react';
import { Clock, User, ChevronRight, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useLanguage } from '@/contexts/LanguageContext';
import { toArabicNumerals } from '@/lib/utils';

interface Article {
  id: string;
  title: {
    ar: string;
    en: string;
  };
  excerpt: {
    ar: string;
    en: string;
  };
  author: string;
  category: string;
  image_url: string;
  read_time: number;
  created_at: string;
}

export function LandingArticles() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const { t, language } = useLanguage();

  // Create infinite loop by triplicating the array (reversed order)
  const infiniteArticles = articles.length > 0 
    ? [...articles.slice().reverse(), ...articles.slice().reverse(), ...articles.slice().reverse()] 
    : [];

  useEffect(() => {
    fetchArticles();
  }, []);

  // Initialize scroll to middle set after articles load
  useEffect(() => {
    if (articles.length > 0 && containerRef.current) {
      const container = containerRef.current;
      // Wait for layout to complete
      setTimeout(() => {
        const cardElement = container.children[0] as HTMLElement;
        if (cardElement) {
          // Scroll to the start of the middle set (second copy)
          const middlePosition = cardElement.offsetWidth * articles.length;
          container.scrollLeft = middlePosition;
        }
      }, 100);
    }
  }, [articles.length]);

  // Monitor scroll position and reset if needed for infinite effect
  useEffect(() => {
    const container = containerRef.current;
    if (!container || articles.length === 0) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const cardElement = container.children[0] as HTMLElement;
      if (!cardElement) return;

      const cardWidth = cardElement.offsetWidth;
      const scrollLeft = container.scrollLeft;
      const totalOriginalWidth = cardWidth * articles.length;

      // If scrolled past the end of second set, jump to start of second set
      if (scrollLeft >= totalOriginalWidth * 2) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
      // If scrolled before the start of second set, jump to end of second set
      else if (scrollLeft < totalOriginalWidth) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth * 2 - cardWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [articles.length]);

  // Auto-scroll effect
  useEffect(() => {
    if (articles.length === 0) return;

    const interval = setInterval(() => {
      if (!isHovering && containerRef.current && !isScrollingRef.current) {
        const container = containerRef.current;
        const cardElement = container.children[0] as HTMLElement;
        if (!cardElement) return;
        
        const scrollAmount = cardElement.offsetWidth;
        
        // Scroll left (decrease scrollLeft) - the infinite scroll handler will take care of wrapping
        container.scrollTo({ 
          left: container.scrollLeft - scrollAmount, 
          behavior: 'smooth' 
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isHovering, articles.length]);

  const fetchArticles = async () => {
    try {
      const articlesQuery = query(
        collection(db, 'landing_articles'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(articlesQuery);

      const articlesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as Article[];

      setArticles(articlesData);
    } catch (err) {
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (direction: 'prev' | 'next') => {
    const container = containerRef.current;
    if (!container) return;

    const cardElement = container.children[0] as HTMLElement;
    if (!cardElement) return;
    
    const scrollAmount = cardElement.offsetWidth;
    
    if (direction === 'next') {
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    } else {
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const formatDate = (date: string) => {
    const formatted = new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    // Convert to Arabic-Indic numerals if in Arabic mode
    return language === 'ar' ? toArabicNumerals(formatted) : formatted;
  };

  const generateArticleHash = (article: Article) => {
    return btoa(article.id).substring(0, 8);
  };

  if (loading) {
    return (
      <section className="py-8 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">{t('landing.common.loading')}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="articles" className="py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-[#004250] mb-12">
          {t('landing.articles.title')}
        </h2>
        
        <div 
          className="relative group"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div 
            ref={containerRef}
            dir="ltr"
            className="flex overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {infiniteArticles.map((article, index) => {
              const title = article.title[language] || article.title.ar;
              const excerpt = article.excerpt[language] || article.excerpt.ar;
              
              // Safe rendering for author and category with proper language selection
              const author = typeof article.author === 'string' 
                ? article.author 
                : (typeof article.author === 'object' ? (article.author[language] || article.author.ar || article.author.en || '') : '');
              const category = typeof article.category === 'string' 
                ? article.category 
                : (typeof article.category === 'object' ? (article.category[language] || article.category.ar || article.category.en || '') : '');
              
              return (
              <div
                key={`${article.id}-${index}`}
                className="min-w-full md:min-w-[33.333%] px-4 py-8"
              >
                <Link
                  to={`/landing/article/${generateArticleHash(article)}`}
                >
                  <article className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 h-full group">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={article.image_url} 
                        alt={title} 
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                      />
                      <span className="absolute top-4 right-4 bg-[#7dda55] text-white px-3 py-1 rounded-full text-sm">
                        {category}
                      </span>
                    </div>
                    <div className="p-6">
                      <h3 className="text-[1.1875rem] font-bold text-[#004250] mb-3 line-clamp-2 group-hover:text-[#7dda55] transition-colors">
                        {title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{article.read_time} {t('landing.articles.min')}</span>
                        </div>
                      </div>
                      <div className="text-gray-500 text-sm">
                        {formatDate(article.created_at)}
                      </div>
                    </div>
                  </article>
                </Link>
              </div>
            )}
            )}
          </div>

          <button
            onClick={() => handleNavigation('next')}
            className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <button
            onClick={() => handleNavigation('prev')}
            className="absolute left-0 top-1/2 -translate-y-1/2 translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        </div>
      </div>
    </section>
  );
}

